import { HelpCircle, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { type Intent, INTENT_LABELS } from '@/lib/intentTypes';

export interface IntentConfirmationBannerProps {
  suggestedIntent: Intent;
  confidence: number;
  reasoning?: string;
  onConfirm: (intent: Intent) => void;
  onCancel: () => void;
  onChangeTo: (intent: Intent) => void;
}

/**
 * Visas när klassificerarens confidence < threshold (0.7 default). User
 * kan bekräfta föreslagen intent eller välja en annan innan AI-call körs.
 */
export function IntentConfirmationBanner({
  suggestedIntent, confidence, reasoning, onConfirm, onCancel, onChangeTo,
}: IntentConfirmationBannerProps) {
  const def = INTENT_LABELS[suggestedIntent];

  const alternatives: Intent[] = [
    'build_new', 'extend', 'modify_in_place', 'refactor', 'revert', 'explain',
  ].filter((i): i is Intent => i !== suggestedIntent);

  return (
    <div
      className="border border-blue-300 bg-blue-50 dark:bg-blue-950/20 rounded-lg p-3 space-y-2"
      data-testid="intent-confirm-banner"
    >
      <div className="flex items-start gap-2">
        <HelpCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-600" />
        <div className="flex-1 text-sm">
          <div className="font-medium">
            Jag tolkar detta som <strong>{def.sv}</strong>.
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Confidence: {Math.round(confidence * 100)}%
            {reasoning && <> — {reasoning}</>}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 items-center">
        <Button
          size="sm"
          variant="default"
          onClick={() => onConfirm(suggestedIntent)}
          className="h-7 gap-1"
        >
          <Check className="w-3.5 h-3.5" />
          Ja, kör
        </Button>
        <span className="text-xs text-muted-foreground">eller välj:</span>
        {alternatives.map((intent) => (
          <Button
            key={intent}
            size="sm"
            variant="outline"
            onClick={() => onChangeTo(intent)}
            className="h-7 text-xs"
          >
            {INTENT_LABELS[intent].sv}
          </Button>
        ))}
        <Button
          size="sm"
          variant="ghost"
          onClick={onCancel}
          className="h-7 gap-1 ml-auto"
          title="Avbryt"
        >
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}
